// src/input/controls.ts -- precision aiming controls (DOM layer, NOT sim/)
// Provides both drag-to-aim AND button-based precision controls.
// Angle and power persist between shots so players can "walk" artillery.

import { GAME_W, GAME_H } from '../render/canvas';
import { HUD_Y, hitTest, WEAPON_ROW_H, WEAPON_PICKER_H, WEAPON_PICKER } from '../render/hud-layout';
import { WEAPONS } from '../sim/weapons';
import type { Tank } from '../sim/state';

export interface AimState {
  angle: number;      // degrees 0-180 (0 = right, 90 = up, 180 = left)
  power: number;      // 0-1000
  fired: boolean;     // one-shot flag, consumer must reset
  isAiming: boolean;  // true while dragging
  aimX: number;       // aim line endpoint (game coords)
  aimY: number;
}

export function newAimState(): AimState {
  return {
    // Classic SE starting aim: 55° (slight lob right) at mid-high power.
    angle: 55,
    power: 500,
    fired: false,
    isAiming: false,
    aimX: 0,
    aimY: 0,
  };
}

/** Convert degrees (0=right, 90=up, 180=left) to radians for sim */
export function aimToRadians(angleDeg: number): number {
  return -(angleDeg * Math.PI) / 180;
}

/** Convert power 0-1000 to 0-1 fraction for sim */
export function aimToPowerFrac(power: number): number {
  return power / 1000;
}

/** Convert screen pixel position to game coordinates */
function screenToGame(canvas: HTMLCanvasElement, clientX: number, clientY: number): { gx: number; gy: number } {
  const rect = canvas.getBoundingClientRect();
  const gx = ((clientX - rect.left) / rect.width) * GAME_W;
  const gy = ((clientY - rect.top) / rect.height) * GAME_H;
  return { gx, gy };
}

const MAX_DRAG_PX = 200;

export interface WeaponPickerInput {
  open: boolean;
  scroll: number;
}

export function newWeaponPickerInput(): WeaponPickerInput {
  return { open: false, scroll: 0 };
}

export function attachControls(
  canvas: HTMLCanvasElement,
  getAim: (() => AimState) | AimState,
  onGearTap?: () => void,
  pickerState?: WeaponPickerInput,
  getCurrentTank?: () => Tank | undefined,
) {
  // Support both getter function and direct AimState for backwards compat
  const aim = typeof getAim === 'function' ? getAim : () => getAim;

  let activeId: number | null = null;
  let startX = 0, startY = 0;

  // Picker drag-scroll state
  const MAX_PICKER_SCROLL = Math.max(0, WEAPONS.length * WEAPON_ROW_H - WEAPON_PICKER_H);
  const SCROLL_TAP_THRESHOLD = 6; // px of movement before treating as scroll
  let pickerPointerId: number | null = null;
  let pickerStartGy = 0;
  let pickerStartScroll = 0;
  let pickerRowIdx = -1;
  let pickerDidScroll = false;

  // --- Drag-to-aim (updates angle/power numbers as you drag) ---
  canvas.addEventListener('pointerdown', (e) => {
    const { gx, gy } = screenToGame(canvas, e.clientX, e.clientY);

    // Check weapon-related interactions first
    const wpAction = hitTest(gx, gy, pickerState?.open);
    if (wpAction === 'weapon-toggle' && pickerState) {
      pickerState.open = !pickerState.open;
      pickerState.scroll = 0;
      e.preventDefault();
      return;
    }
    if (wpAction?.startsWith('weapon-pick:') && pickerState) {
      // Start a pick/scroll gesture — don't commit the pick until pointerup.
      // Compute actualIdx from gy + current scroll to avoid mis-clicks when
      // scroll is not a multiple of WEAPON_ROW_H.
      const actualIdx = Math.floor((gy - WEAPON_PICKER.y + pickerState.scroll) / WEAPON_ROW_H);
      pickerPointerId = e.pointerId;
      pickerStartGy = gy;
      pickerStartScroll = pickerState.scroll;
      pickerRowIdx = actualIdx;
      pickerDidScroll = false;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      e.preventDefault();
      return;
    }
    // If picker is open, close it on any tap outside
    if (pickerState?.open) {
      pickerState.open = false;
      e.preventDefault();
      return;
    }

    if (gy >= HUD_Y) return; // let button handler deal with HUD taps

    // Check gear icon tap (top-right corner)
    const gearAction = hitTest(gx, gy);
    if (gearAction === 'gear' && onGearTap) {
      onGearTap();
      e.preventDefault();
      return;
    }

    if (activeId !== null) return;
    activeId = e.pointerId;
    canvas.setPointerCapture(e.pointerId);
    startX = e.clientX;
    startY = e.clientY;
    aim().isAiming = true;
    e.preventDefault();
  });

  canvas.addEventListener('pointermove', (e) => {
    // Picker scroll takes priority
    if (e.pointerId === pickerPointerId && pickerState) {
      const { gy } = screenToGame(canvas, e.clientX, e.clientY);
      const dy = gy - pickerStartGy;
      if (Math.abs(dy) >= SCROLL_TAP_THRESHOLD) pickerDidScroll = true;
      // Drag down = scroll content down (reveal items above); subtract dy
      const next = pickerStartScroll - dy;
      pickerState.scroll = Math.max(0, Math.min(MAX_PICKER_SCROLL, next));
      e.preventDefault();
      return;
    }
    if (e.pointerId !== activeId) return;
    const a = aim();
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    const rawAngle = Math.atan2(-dy, -dx);
    const deg = (rawAngle * 180) / Math.PI;
    a.angle = Math.max(0, Math.min(180, Math.round(deg)));
    a.power = Math.max(0, Math.min(1000, Math.round(
      (Math.min(Math.hypot(dx, dy) / MAX_DRAG_PX, 1)) * 1000
    )));
    e.preventDefault();
  });

  function endDrag(e: PointerEvent) {
    // Picker gesture end: commit pick if it was a tap, else just release
    if (e.pointerId === pickerPointerId && pickerState) {
      if (!pickerDidScroll && getCurrentTank) {
        const weapon = WEAPONS[pickerRowIdx];
        const tank = getCurrentTank();
        if (weapon && tank) {
          const qty = tank.inventory[weapon.id] ?? 0;
          if (qty > 0 || weapon.isFree) {
            tank.selectedWeapon = weapon.id;
          }
        }
        pickerState.open = false;
      }
      pickerPointerId = null;
      pickerRowIdx = -1;
      pickerDidScroll = false;
      return;
    }
    if (e.pointerId !== activeId) return;
    aim().isAiming = false;
    activeId = null;
  }
  canvas.addEventListener('pointerup', endDrag);
  canvas.addEventListener('pointercancel', endDrag);

  // --- Mouse wheel scroll for the weapon picker (desktop) ---
  canvas.addEventListener('wheel', (e) => {
    if (!pickerState?.open) return;
    const { gx, gy } = screenToGame(canvas, e.clientX, e.clientY);
    // Only consume wheel when over the picker area
    if (gx < WEAPON_PICKER.x || gx > WEAPON_PICKER.x + WEAPON_PICKER.w) return;
    if (gy < WEAPON_PICKER.y || gy > WEAPON_PICKER.y + WEAPON_PICKER_H) return;
    // deltaY > 0 = scroll down (reveal items below) = increase scroll
    pickerState.scroll = Math.max(0, Math.min(MAX_PICKER_SCROLL, pickerState.scroll + e.deltaY));
    e.preventDefault();
  }, { passive: false });

  // --- Keyboard controls ---
  // Default step: 10 power / 5 angle. Shift -> 100 power / 10 angle. Alt -> 1 / 1.
  window.addEventListener('keydown', (e) => {
    // Ignore when typing in an input field
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const a = aim();
    const powerStep = e.shiftKey ? 100 : e.altKey ? 1 : 10;
    const angleStep = e.shiftKey ? 10 : e.altKey ? 1 : 5;
    switch (e.key) {
      case 'ArrowLeft':
        a.angle = Math.min(180, a.angle + angleStep); e.preventDefault(); break;
      case 'ArrowRight':
        a.angle = Math.max(0, a.angle - angleStep); e.preventDefault(); break;
      case 'ArrowUp':
        a.power = Math.min(1000, a.power + powerStep); e.preventDefault(); break;
      case 'ArrowDown':
        a.power = Math.max(0, a.power - powerStep); e.preventDefault(); break;
      case ' ':
      case 'Enter':
        a.fired = true; e.preventDefault(); break;
    }
  });

  // --- Button controls using shared hit testing from hud-layout.ts ---
  canvas.addEventListener('pointerdown', (e) => {
    const { gx, gy } = screenToGame(canvas, e.clientX, e.clientY);
    if (gy < HUD_Y) return; // not in HUD

    const action = hitTest(gx, gy);
    if (!action) return;
    e.preventDefault();

    const a = aim();
    switch (action) {
      case 'fire':     a.fired = true; break;
      // Left buttons aim more left = increase angle toward 180
      // Right buttons aim more right = decrease angle toward 0
      case 'angle-5':  a.angle = Math.min(180, a.angle + 5); break;
      case 'angle-1':  a.angle = Math.min(180, a.angle + 1); break;
      case 'angle+1':  a.angle = Math.max(0, a.angle - 1); break;
      case 'angle+5':  a.angle = Math.max(0, a.angle - 5); break;
      case 'power-100': a.power = Math.max(0, a.power - 100); break;
      case 'power-10': a.power = Math.max(0, a.power - 10); break;
      case 'power-1':  a.power = Math.max(0, a.power - 1); break;
      case 'power+1':  a.power = Math.min(1000, a.power + 1); break;
      case 'power+10': a.power = Math.min(1000, a.power + 10); break;
      case 'power+100': a.power = Math.min(1000, a.power + 100); break;
    }
  });
}
